"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
exports.__esModule = true;
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _styles = require("@mui/material/styles");
var _jss = require("jss");
var _jssRtl = _interopRequireDefault(require("jss-rtl"));
var _styles2 = require("@mui/styles");
var _react2 = require("@emotion/react");
var _cache = _interopRequireDefault(require("@emotion/cache"));
var _stylis = require("stylis");
var _stylisPluginRtl = _interopRequireDefault(require("stylis-plugin-rtl"));
var _lodash = require("lodash");
var _cloneDeep = _interopRequireDefault(require("lodash/cloneDeep"));
var _themes = require("@ellucian/react-design-system/core/themes");
var _core = require("@ellucian/react-design-system/core");
var _styles3 = require("../styles/");
var _EDSContext = require("../EDSContext/EDSContext");
var _CssBaseline = _interopRequireDefault(require("./internal/CssBaseline"));
var _globalSpacing = require("./../globalSpacing");
var _edsconfig = _interopRequireDefault(require("./internal/edsconfig.js"));
exports.config = _edsconfig.default;
var _urls = _interopRequireDefault(require("./internal/urls"));
exports.cdn = _urls.default;
// JSS

// MUI Required style-related for JSS

// Emotion

// Stylis/Emotion Related

var DEFAULT_DIR = 'ltr';
var SUPPORTED_LOCALES = ['ar', 'en-AU', 'en-GB', 'en-US', 'es', 'es-CL', 'es-MX', 'es-CO', 'es-EC', 'es-PE', 'es-DO', 'es-PR', 'fr', 'fr-CA'];

/**
 * Given an incoming locale, returns a locale string in the correct format, representing one of the locales
 * that Path supports.
 *
 * For example, if the incoming locale is es_MX
 *
 * If `es_MX` locale is available, then use it
 * If `es_MX` locale is not available, then use `es` language
 * If `es` language is not available, then use `en_US` (i.e. Default locale)
 */
function normalizeLocale(locale) {
  var normalizedLocale = _EDSContext.DEFAULT_LOCALE;
  if (!(0, _lodash.isNil)(locale)) {
    // replace underscore ('_') with hyphen ('-') to support hyphens in locale value.
    var normalized = locale.replace(/_/g, '-').toLowerCase();
    normalizedLocale = findLocale(normalized);
  }
  return normalizedLocale;
}
function findLocale(locale) {
  var match = SUPPORTED_LOCALES.filter(function (supported) {
    return supported.toLowerCase() === locale;
  })[0];
  if (match) {
    return match;
  }
  var tags = locale.split('-');
  if (tags.length > 1) {
    return findLocale(tags.slice(0, -1).join('-'));
  }
  return _EDSContext.DEFAULT_LOCALE;
}

/**
 * `EDSApplication` is the top-level component of an EDS application. All apps must be wrapped in this component.
 *
 * @done true
 * @updated v7.12.0
 * @versionAdded v0.0.13
 * @examples
 *  EDSApplicationExample
 */
var EDSApplication = function EDSApplication(props) {
  var parentContext = (0, _core.useEDSInfo)();
  var copiedTheme = (0, _cloneDeep.default)(props.theme); // never mutate props

  var locale = normalizeLocale(props.locale);
  if ((0, _lodash.isNil)(parentContext) || (0, _lodash.isNil)(parentContext.dir)) {
    // if empty context, then this is the first and parent EDSApplication instance
    // therefore, we need to initialize emotion and jss styles and load proper plugins according to dir

    // configure emotion (MUI base styles)
    var cache = _react.default.useMemo(
    //eslint-disable-line react-hooks/rules-of-hooks
    function () {
      return (0, _cache.default)({
        key: "eds-" + props.dir,
        prepend: true,
        insertionPoint: document.querySelector('meta[charset]'),
        stylisPlugins: props.dir === 'rtl' ? [_stylis.prefixer, _stylisPluginRtl.default] : [_stylis.prefixer]
      });
    }, [props.dir]);

    // Configure JSS (our custom style overrides)
    var jss = _react.default.useMemo(
    //eslint-disable-line react-hooks/rules-of-hooks
    function () {
      return (0, _jss.create)({
        plugins: [].concat((0, _styles2.jssPreset)().plugins, [(0, _jssRtl.default)({
          enabled: props.dir === 'rtl'
        })])
      });
    }, [props.dir]);

    // Set direction prop in the theme
    copiedTheme.direction = props.dir;
    return /*#__PURE__*/_react.default.createElement(_EDSContext.EDSContext.Provider, {
      value: {
        dir: props.dir,
        locale: locale,
        labelOverrides: props.labelOverrides,
        platform: props.platform
      }
    }, /*#__PURE__*/_react.default.createElement(_styles.StyledEngineProvider, {
      injectFirst: true
    }, "             ", /*#__PURE__*/_react.default.createElement(_react2.CacheProvider, {
      value: cache
    }, "               ", /*#__PURE__*/_react.default.createElement(_styles2.StylesProvider, {
      generateClassName: _styles3.classNameGenerator,
      jss: jss
    }, /*#__PURE__*/_react.default.createElement(_styles.ThemeProvider, {
      theme: copiedTheme
    }, !props.excludeCssBaseline && /*#__PURE__*/_react.default.createElement(_CssBaseline.default, null), !props.excludeCssGlobalSpacing && /*#__PURE__*/_react.default.createElement(_globalSpacing.CssGlobalSpacing, null), props.children)))));
  } else {
    // else, context already exists, we are a child EDSApplication
    // we do not need to re-initialize the emotion/jss styles, since that is provided by the first rendered EDSApplication
    // Child EDSApplication instances require the props to be set explicitly or else it will inherit the default prop values
    // which may cause unwanted styles showing up

    // Set direction prop in the theme
    copiedTheme.direction = props.dir;
    return /*#__PURE__*/_react.default.createElement(_EDSContext.EDSContext.Provider, {
      value: {
        dir: props.dir,
        locale: locale,
        platform: props.platform,
        labelOverrides: props.labelOverrides
      }
    }, /*#__PURE__*/_react.default.createElement(_styles.ThemeProvider, {
      theme: copiedTheme
    }, props.children));
  }
};
EDSApplication.muiName = 'EDSApplication';
EDSApplication.displayName = 'EDSApplication';
EDSApplication.propTypes = {
  /**
   * The nodes comprising the wrapped application.
   */
  children: _propTypes.default.node.isRequired,
  /**
   * Directionality of the application.
   */
  dir: _propTypes.default.oneOf(['ltr', 'rtl']),
  /**
   * If set to `true`, `CssBaseline` will not be rendered.
   *
   * ** Setting this to `true` may cause unexpected styling to the components. **
   */
  excludeCssBaseline: _propTypes.default.bool,
  /**
    * If set to `true`, `CssGlobalSpacing` will not be rendered.
    *
    * ** Setting this to `true` may cause unexpected styling to the components. **
    */
  excludeCssGlobalSpacing: _propTypes.default.bool,
  /**
   * Overrides for the translated labels.
   */
  labelOverrides: _propTypes.default.object,
  /**
   * Locale to use for translations.
   */
  locale: _propTypes.default.string,
  /**
   * The application's theme.
   */
  theme: _propTypes.default.object,
  /**
   * The platform where components are rendered.
   *
   * Platforms can be a web browser or mobile.
   *
   * Some components' look, feel, and behaviors change between the web and mobile views.
   */
  platform: _propTypes.default.oneOf(['web', 'mobile'])
};

// If default props are needed, they must be set here
EDSApplication.defaultProps = {
  dir: DEFAULT_DIR,
  labelOverrides: {},
  locale: _EDSContext.DEFAULT_LOCALE,
  theme: (0, _themes.createEDSTheme)('light'),
  platform: 'web'
};
var _default = exports.default = EDSApplication;